import EntityModel from '@/common/models/entity-model';
import ContactModel from '@/common/models/contact-model';
import PaymentInstrumentModel from '@/common/models/payment-instrument-model';

export default class PaymentInstrumentExpandedModel extends EntityModel {
    static Methods = PaymentInstrumentModel.methods;

    hydrate({
        // shared fields
        id = null,
        bankName = null,
        last4 = null,
        createdTime = null,
        status = null,
        updatedTime = null,
        customerId = null,
        customFields = null,
        // payment card
        bankCountry = null,
        billingAddress = null,
        billingContact = null,
        bin = null,
        pan = null,
        cvv = null,
        brand = null,
        customer = null,
        expMonth = null,
        expYear = null,
        panFingerprint = null,
        stickyGatewayAccountId = null,
        // bank account
        accountType = null,
        accountNumber = null,
        address = null,
        contactId = null,
        name = null,
        routingNumber = null,
        fingerprint = null,
        // PayPal
        username = null,
        // Cash
        receivedBy = null,
        // Check
        reference = null,
        // Other
        method = null,
    } = {}) {
        this.id = id;
        this.createdTime = createdTime;
        this.status = status;
        this.updatedTime = updatedTime;
        this.customerId = customerId;
        this.customFields = customFields;

        // Shared between 2 of the three
        // PayPal or Payment Card
        if (username || bin) {
            this.billingAddress = billingAddress;
        }
        // Payment Card or Bank Account
        if (bin || pan || routingNumber) {
            this.bankName = bankName;
            this.last4 = last4;
        }

        // Credit Card
        if (bin) {
            this.bankCountry = bankCountry;
            this.billingContact = new ContactModel(billingContact);
            this.bin = bin;
            this.brand = brand;
            this.customer = customer;
            this.expMonth = expMonth;
            this.expYear = expYear;
            this.panFingerprint = panFingerprint;
            this.stickyGatewayAccountId = stickyGatewayAccountId;
        }
        if (pan) {
            this.billingAddress = billingAddress;
            this.brand = brand;
            this.pan = pan;
            this.expMonth = expMonth;
            this.expYear = expYear;
            // only when creating
            this.cvv = cvv;
        }

        // Bank Account
        if (routingNumber) {
            this.accountType = accountType;
            this.accountNumber = accountNumber;
            this.address = address;
            this.contactId = contactId;
            this.name = name;
            this.routingNumber = routingNumber;
            this.fingerprint = fingerprint;
        }

        // PayPal
        if (username) {
            this.username = username;
        }

        // Cash
        if (receivedBy) {
            this.receivedBy = receivedBy;
        }

        // Check
        if (reference) {
            this.reference = reference;
        }

        if (method) {
            this.method = method;
        }
    }

    getMethod() {
        return PaymentInstrumentModel.getPaymentMethod({...this});
    }

    getPaymentInstrumentModelPayload() {
        let payload = {
            method: this.getMethod(),
        };
        switch (payload.method) {
            case PaymentInstrumentModel.Methods.paymentCard:
                payload = {
                    ...payload,
                    paymentCardId: this.id,
                };
                break;
            case PaymentInstrumentModel.Methods.ach:
                payload = {
                    ...payload,
                    bankAccountId: this.id,
                };
                break;
            case PaymentInstrumentModel.Methods.paypal:
                payload = {
                    ...payload,
                    payPalAccountId: this.id,
                };
                break;
            case PaymentInstrumentModel.Methods.cash:
                payload = {
                    ...payload,
                    receivedBy: this.receivedBy,
                };
                break;
            case PaymentInstrumentModel.Methods.check:
            default:
                payload = {
                    ...payload,
                    reference: this.reference,
                };
                break;
        }
        return payload;
    }

    get isPaymentCard() {
        return !!this.bin;
    }

    getPayload() {
        let instrument = null;
        if (this.expMonth && this.expYear) {
            if (this.pan) { // creating payment card
                instrument = {
                    billingAddress: this.billingAddress,
                    pan: this.pan,
                    brand: this.brand,
                    expMonth: this.expMonth,
                    expYear: this.expYear,
                    cvv: this.cvv,
                };
            } else {
                instrument = {
                    bankCountry: this.bankCountry,
                    billingAddress: this.billingAddress,
                    billingContact: this.billingContact,
                    bin: this.bin,
                    brand: this.brand,
                    customer: this.customer,
                    expMonth: this.expMonth,
                    expYear: this.expYear,
                    last4: this.last4,
                    panFingerprint: this.panFingerprint,
                    stickyGatewayAccountId: this.stickyGatewayAccountId,
                };
            }
        }
        if (this.routingNumber) {
            instrument = {
                accountType: this.accountType,
                address: this.address,
                contactId: this.contactId,
                name: this.name,
                routingNumber: this.routingNumber,
            };
            if (this.accountNumber) {
                instrument = {
                    ...instrument,
                    accountNumber: this.accountNumber,
                };
            }
        }
        if (this.username) {
            instrument = {
                username: this.username,
                billingAddress: this.billingAddress,
            };
        }

        if (instrument
            && instrument.address
            && instrument.address instanceof ContactModel) {
            instrument.address = instrument.address.getPayload();
        }

        if (instrument
            && instrument.billingContact
            && instrument.billingContact instanceof ContactModel) {
            instrument.billingContact = instrument.billingContact.getPayload();
        }

        return this.removeNullPayloadValues({
            id: this.id,
            bankName: this.bankName,
            createdTime: this.createdTime,
            status: this.status,
            updatedTime: this.updatedTime,
            customerId: this.customerId,
            customFields: this.customFields,
            ...instrument || {}, // the instrument is null by default
        });
    }
}
